import * as _ from "lodash";

import { ISdUiApi, SdUiApi } from "./sdApiUi";
import { ISdDebugApi, SdDebugApi } from "./sdApiDebug"
import { ViewerUtils } from "../common/viewerUtils";
import * as SmartDrawings from "../smartDrawings";
import { selectByGuid, viewerOptions } from "../viewer";

let forgeViewer: Autodesk.Viewing.GuiViewer3D;

interface ISdApi {
    entities: ISdEntitiesApi;
    ui: ISdUiApi;
    debug: ISdDebugApi;
}

export class SdApi implements ISdApi {
    entities: ISdEntitiesApi;
    ui: ISdUiApi;
    debug: ISdDebugApi;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D) {
        forgeViewer = viewer;
        this.entities = new SdEntitiesApi();
        this.ui = new SdUiApi(forgeViewer);
        this.debug = new SdDebugApi(forgeViewer);
    }
}

interface ISdEntitiesApi {
    isEnabled(): boolean;
    getSelection(): Promise<string[]>;
    getSelectionOfType(type: string): Promise<string[]>;
    setSelection(ids: string[]): boolean;
    addToSelection(id: string): boolean;

    /**
     * Set color to object
     * @param id
     * @param red 0-255
     * @param green 0-255
     * @param blue 0-255
     * @param alpha 0-255
     */
    setColor(id: string, red: number, green: number, blue: number, alpha: number): boolean;

    /**
     * Set color to objects
     * @param ids id list
     */
    setColors(ids: string[], red: number, green: number, blue: number, alpha: number): boolean;

    resetColor(id: string): boolean;
    resetColors(): boolean;
    getMetadata(id: string, group: string, property: string): Promise<string>;
    errorMessage: string;
}

class SdEntitiesApi implements ISdEntitiesApi {
    errorMessage: string;

    constructor() {
        this.errorMessage = "";
    }

    isEnabled(): boolean {
        return !viewerOptions.idMap.isEmpty();
    }

    async getSelection(): Promise<string[]> {
        let result: string[] = [];
        this.errorMessage = "";

        if (viewerOptions.idMap.isEmpty()) {
            this.errorMessage = "Id mapping didn't exist";
            return null;
        }

        try {
            let dbIds = forgeViewer.getSelection();
            for (let dbId of dbIds) {
                result.push(await this.#getGuid(dbId));
            }
        } catch (e) {
            this.errorMessage = (e as Error).message;
            result = null;
        }

        return result;
    }

    async getSelectionOfType(type: string): Promise<string[]> {
        let result: string[] = [];
        this.errorMessage = "";

        if (viewerOptions.idMap.isEmpty()) {
            this.errorMessage = "Id mapping didn't exist";
            return null;
        }

        try {
            let dbIds = forgeViewer.getSelection();
            for (let dbId of dbIds) {
                let id = await this.#isSameType(dbId, type);
                if (id !== null) {
                    let guid = await this.#getGuid(id, false);
                    if (guid)
                        result.push(guid);
                }
            }
        } catch (e) {
            this.errorMessage = (e as Error).message;
            result = null;
        }

        if (result !== null && result.length > 0)
            result = _.uniq(result);

        return result;
    }

    async #getGuid(dbId: number, recursive: boolean = true) {
        let result = viewerOptions.idMap.guid(null, dbId);
        if (recursive && !result) {
            let parentId = await ViewerUtils.getNodeParent(dbId, forgeViewer);
            if (parentId != null) {
                return await this.#getGuid(parentId);
            }
        }

        return result;
    }

    async #isSameType(dbId: number, type: string): Promise<number> {
        // Fetch type property (assume type property is in Item category)
        var p = await this.#getPropertiesFromModel(dbId);
        var value = _.find(p, prop => prop.displayCategory === "Item" && prop.displayName === "Type");

        let result = false;

        if (value && value.displayValue.toString().toLowerCase() === type.toLowerCase())
            return dbId;

        // Try to find same type in parent node
        let parentId = await ViewerUtils.getNodeParent(dbId, forgeViewer);
        if (parentId != null)
            return await this.#isSameType(parentId, type);

        return null;
    }

    setSelection(ids: string[]): boolean {
        let result = true;
        this.errorMessage = "";

        if (viewerOptions.idMap.isEmpty()) {
            this.errorMessage = "Id mapping didn't exist";
            return false;
        }

        try {
            selectByGuid(ids);
        } catch (e) {
            this.errorMessage = (e as Error).message;
            result = false;
        }

        return result;
    }

    addToSelection(id: string): boolean {
        let result = true;
        this.errorMessage = "";

        if (viewerOptions.idMap.isEmpty()) {
            this.errorMessage = "Id mapping didn't exist";
            return false;
        }

        try {
            let dbId: number = viewerOptions.idMap.nodeId(null, id);
            if (!dbId) {
                this.errorMessage = `Cannot find element with id: ${id}`;
                return false;
            }

            var selected = forgeViewer.getSelection();
            if (!_.includes(selected, dbId)) {
                selected.push(dbId);
                forgeViewer.select(selected);
            }

        } catch (e) {
            this.errorMessage = (e as Error).message;
            result = false;
        } 

        return result;
    }

    setColor(id: string, red: number, green: number, blue: number, alpha: number): boolean {
        return this.setColors([id], red, green, blue, alpha);
    }

    setColors(ids: string[], red: number, green: number, blue: number, alpha: number): boolean {
        let result = true;
        this.errorMessage = "";

        if (viewerOptions.idMap.isEmpty()) {
            this.errorMessage = "Id mapping didn't exist";
            return false;
        }

        red = red < 0 ? 0 : red > 255 ? 255 : red;
        green = green < 0 ? 0 : green > 255 ? 255 : green;
        blue = blue < 0 ? 0 : blue > 255 ? 255 : blue;
        alpha = (alpha < 0 ? 0 : alpha > 255 ? 255 : alpha) / 255;

        try {
            var model = forgeViewer.getVisibleModels()[0];
            const c = new THREE.Color(`rgb(${red}, ${green}, ${blue})`);
            let colorVector: THREE.Vector4 = new THREE.Vector4(c.r, c.g, c.b, alpha);

            let iColored: number = 0;
            for (let id of ids) {
                let dbId: number = viewerOptions.idMap.nodeId(null, id);
                if (!dbId) {
                    this.errorMessage = `Cannot find element with id: ${id}`;
                    return false;
                }

                //forgeViewer.setThemingColor(dbId, colorVector, forgeViewer.getVisibleModels()[0], true);
                model.setThemingColor(dbId, colorVector, true);
                iColored++;
            }

            if (iColored > 0) {
                forgeViewer.impl.invalidate(true);
            }
        } catch (e) {
            this.errorMessage = (e as Error).message;
            result = false;
        }

        return result;
    }

    resetColor(id: string): boolean {
        let result = true;
        this.errorMessage = "";

        if (viewerOptions.idMap.isEmpty()) {
            this.errorMessage = "Id mapping didn't exist";
            return false;
        }

        try {
            let dbId: number = viewerOptions.idMap.nodeId(null, id);
            if (!dbId) {
                this.errorMessage = `Cannot find element with id: ${id}`;
                return false;
            }

            forgeViewer.setThemingColor(dbId, null, forgeViewer.getVisibleModels()[0], true);
        } catch (e) {
            this.errorMessage = (e as Error).message;
            result = false;
        }

        return result;
    }

    resetColors(): boolean {
        let result = true;
        this.errorMessage = "";

        try {
            const models = forgeViewer.getVisibleModels();
            for (let model of models) {
                forgeViewer.clearThemingColors(model);
            }
        } catch (e) {
            this.errorMessage = (e as Error).message;
            result = false;
        }

        return result;
    }

    async getMetadata(id: string, group: string, property: string): Promise<string> {
        if (viewerOptions.idMap.isEmpty()) {
            this.errorMessage = "Id mapping didn't exist";
            return null;
        }

        const dbId = viewerOptions.idMap.nodeId(null, id);

        var p = await this.#getPropertiesFromModel(dbId);
        var value = _.find(p, prop => prop.displayCategory === group && prop.displayName === property);

        if (value)
            return value.displayValue.toString();
        else
            return null;
    }

    #getPropertiesFromModel = (nodeId): Promise<Autodesk.Viewing.Property[]> => {
        return new Promise((resolve, reject) => {
            forgeViewer.model.getProperties(
                nodeId,
                result => resolve(result.properties),
                error => reject(error));
        });
    }
}